import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import classNames from 'classnames';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import Calendar from '../../Calendar';
import CalendarNew from '../../CalendarNew';

import useOnClickOutside from '../../../hooks/useOnClickOutside';
import classes from './styles.module.scss';

function convertToDateIfNeeded(value) {
  if (value instanceof Date) {
    return value;
  }
  const date = new Date(value);
  // eslint-disable-next-line no-restricted-globals
  if (!isNaN(date.getTime())) {
    return date;
  }
  return value;
}

export default function DatePickerSwitch({
  name,
  value,
  setFieldValue,
  touched,
  error,
  setFieldTouched,
  dateFieldWidth,
  label,
  isPopupOpened,
  height,
  labelStyle,
  style,
  width,
  calendarPosition,
  onlyPastDates,
  warning,
  staticErrorPositon,
  readonly,
  newCalendar,
  calendarDefaultDate,
  // Switch-specific props
  switchName,
  switchValue,
  switchLabel,
  onSwitchChange,
  switchReadOnly,
}) {
  const [isCalendarVisible, setIsCalendarVisible] = useState(false);
  const [portalPosition, setPortalPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  const calendarRef = useRef();
  const valueRef = useRef();
  const componentRef = useRef();

  const { t } = useTranslation();

  useOnClickOutside(
    calendarRef,
    () => setIsCalendarVisible(false),
    componentRef
  );

  useEffect(() => {
    if (calendarPosition) {
      return;
    }
    if (isCalendarVisible && !isPopupOpened && valueRef.current) {
      const coords = valueRef.current.getBoundingClientRect();
      setPortalPosition({
        top: coords.bottom + window.scrollY + 10,
        left: coords.left + window.scrollX,
        width: coords.width,
      });
    }
  }, [isCalendarVisible, isPopupOpened, calendarPosition]);

  const setValue = (date) => {
    setFieldValue?.(name, date);
    setTimeout(() => {
      setFieldTouched?.(name, true);
    });
  };

  const handleSwitchToggle = (e) => {
    e?.stopPropagation?.();
    const next = !switchValue;
    onSwitchChange?.(next);
    if (switchName && setFieldValue) {
      setFieldValue(switchName, next);
      setTimeout(() => setFieldTouched?.(switchName, true));
    }
  };

  const dateValue = convertToDateIfNeeded(value);

  useEffect(() => {
    setIsCalendarVisible(false);
  }, [value]);

  return (
    <div
      className={classNames(classes.DatePickerSwitchRoot, {
        [classes.withFieldWidth]: !!dateFieldWidth,
        [classes.withCalendarPosition]: !!calendarPosition,
        [classes.staticErrorPositon]: staticErrorPositon,
      })}
      ref={componentRef}
    >
      <div className={classes.labelRow}>
        <h2 style={labelStyle}>{label}</h2>
        {typeof switchValue === 'boolean' && (
          <button
            type="button"
            role="switch"
            aria-checked={switchValue}
            className={classNames(classes.switchControl, {
              [classes.enabled]: switchValue,
            })}
            onClick={handleSwitchToggle}
            disabled={switchReadOnly}
          >
            {switchLabel && (
              <span className={classes.switchText}>{switchLabel}</span>
            )}
            <span className={classes.track}>
              <span className={classes.thumb} />
            </span>
          </button>
        )}
      </div>

      <div
        style={{ width: dateFieldWidth }}
        className={classNames(classes.dateContainer, {
          [classes.calendarOpen]: isCalendarVisible,
          [classes.readonly]: readonly,
        })}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !readonly) {
            setIsCalendarVisible(!isCalendarVisible);
          }
        }}
        onClick={() => !readonly && setIsCalendarVisible(!isCalendarVisible)}
      >
        <div
          className={classes.value}
          ref={valueRef}
          style={{ ...style, height, width }}
        >
          {dateValue ? (
            moment(dateValue)?.format('DD MMMM YYYY')
          ) : (
            <span className={classes.placeholder}>
              {t('common.selectDate')}
            </span>
          )}
        </div>
      </div>

      {isCalendarVisible &&
        createPortal(
          <div
            className={classes.calendarPortal}
            style={{
              position: 'fixed',
              top: `${portalPosition.top}px`,
              left: `${portalPosition.left}px`,
              zIndex: 99999,
            }}
          >
            {newCalendar ? (
              <CalendarNew
                calendarRef={calendarRef}
                value={value === 'Invalid DateTime' ? new Date() : dateValue}
                setValue={setValue}
                onlyPastDates={onlyPastDates}
                setIsCalendarVisible={setIsCalendarVisible}
                calendarDefaultDate={calendarDefaultDate}
              />
            ) : (
              <Calendar
                calendarRef={calendarRef}
                value={value === 'Invalid DateTime' ? new Date() : dateValue}
                setValue={setValue}
                onlyPastDates={onlyPastDates}
              />
            )}
          </div>,
          document.body
        )}

      {touched && error && <div className={classes.error}>{error}</div>}
      {!error && warning && <div className={classes.warning}>{warning}</div>}
    </div>
  );
}
